import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  currentMonthYyyyMm,
  earliestRevenueDetailsMonth,
  REVENUE_DETAILS_VISIBLE_MONTHS,
} from '@uo/shared';
import { usersAPI } from '../api/users';
import { useViewAsAccountParams } from '../store/activeAccount';
import { MonthPickerSheet } from '../components/ui/MonthPickerSheet';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C } from '../components/ui/ThemedSurface';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';

function currentMonth(): string {
  return currentMonthYyyyMm();
}

function monthLabel(month: string): string {
  const part = month.split('-')[1] ?? '';
  return String(Number.parseInt(part, 10) || part);
}

export function IncomeDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth);
  const [pickerOpen, setPickerOpen] = useState(false);
  const viewAsParams = useViewAsAccountParams();
  const viewAccountKey = viewAsParams?.account_id ?? 'main';

  const minMonth = earliestRevenueDetailsMonth();
  const maxMonth = currentMonthYyyyMm();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['users', 'revenue-details', 'month', month, viewAccountKey],
    queryFn: () => usersAPI.getRevenueDetailsMonth(month, viewAsParams),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const weeks = data?.list ?? [];

  const summary = useMemo(() => {
    if (!data) return '';
    return t('incomeDetails.monthTotal', {
      month: monthLabel(month),
      sum: data.sum,
    });
  }, [data, month, t]);

  return (
    <div className="min-h-full" style={{ background: '#131f2f' }}>
      <ThemedPageHeader title={t('incomeDetails.title')} onBack={() => navigate(-1)} theme={C} />

      <MonthPickerSheet
        open={pickerOpen}
        value={month}
        onClose={() => setPickerOpen(false)}
        onConfirm={setMonth}
        minMonth={minMonth}
        maxMonth={maxMonth}
      />

      <div className="px-5 pb-8 pt-[68px]">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="mb-4 flex w-full touch-manipulation items-center justify-between rounded-2xl px-4 py-3.5 text-sm text-white active:opacity-90"
          style={{ background: '#222d3f', border: '1px solid rgba(94, 234, 212, 0.25)' }}
        >
          <span className="text-white/80">{t('incomeDetails.queryDate')}</span>
          <span className="flex items-center gap-2">
            <span className="text-base font-semibold" style={{ color: '#5eead4' }}>
              {month || t('incomeDetails.pickMonth')}
            </span>
            <span className="text-white/50">›</span>
          </span>
        </button>
        <p className="mb-4 text-[11px] leading-relaxed text-white/45">
          {t('incomeDetails.recentMonthsHint', {
            count: REVENUE_DETAILS_VISIBLE_MONTHS,
            defaultValue: `仅可查询最近 ${REVENUE_DETAILS_VISIBLE_MONTHS} 个月`,
          })}
        </p>
        {isError && (
          <QueryErrorNote message={t('incomeDetails.loadFailed')} onRetry={() => void refetch()} />
        )}

        {isLoading && !data ? (
          <div className="space-y-2">
            <LoadingSkeleton className="h-6" />
            <LoadingSkeleton className="h-40" />
          </div>
        ) : (
          <>
            {data?.list && (
              <p className={`mb-4 text-sm text-white/90${isFetching ? ' opacity-70' : ''}`}>{summary}</p>
            )}
            <div className="overflow-hidden rounded-2xl p-4" style={{ background: '#222d3f' }}>
              {weeks.map((week, index) => (
                <button
                  key={`${week.week}-${week.start}`}
                  type="button"
                  onClick={() =>
                    navigate(
                      `/income-details/week?type=${encodeURIComponent(week.week)}&start=${encodeURIComponent(week.start)}&end=${encodeURIComponent(week.end)}`,
                    )
                  }
                  className="flex w-full items-center justify-between border-b py-4 text-left text-white last:border-b-0"
                  style={{ borderColor: '#131f2f' }}
                >
                  <div>
                    <div className="text-sm font-medium">{week.week}</div>
                    <div className="mt-1 text-xs text-white/55">
                      {week.start}-{week.end}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>{week.ep}</span>
                    <span className="text-white/45">›</span>
                  </div>
                </button>
              ))}
              {weeks.length === 0 && (
                <div className="py-8 text-center text-xs text-white/50">{t('common.noData')}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default IncomeDetailsPage;
